import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Campaign } from '../../services/campaign.service';

interface Offer {
  id: string;
  name: string;
  description: string;
  offerType: string;
  discountValue: string;
  promoCode: string;
  validUntil: string;
  carWashType: string;
  location: string;
  maxDiscountAmount: string;
  max_discount: string;
  quantityLimit: number;
  usage: string;
  usageLimit: string;
  status: 'active' | 'expired' | 'scheduled';
}

/**
 * Displays offer information derived from a Campaign object and provides utilities to format and navigate to the campaign offer editor.
 * @component
 * @selector app-campaign-offers
 * @example
 *   <app-campaign-offers [campaign]="sampleCampaign"></app-campaign-offers>
 * @input {Campaign} campaign - The campaign model used to generate the offers list and all display data (id, offer_name, offer_description, offer_type, max_discount, promo_code, start_date, offer_wash_type, location_list, location, quantity_limit, usage_type, campaign_status, etc.).
 * @output {none} none - This component does not emit any output events.
 *
 * Key responsibilities:
 * - Transform the provided Campaign into a normalized Offer[] via the offers getter for template consumption.
 * - Provide presentation helpers to map campaign status values to CSS classes and human-readable labels.
 * - Build a readable location tooltip string for display when multiple locations exist.
 * - Normalize text (remove underscores) for nicer display.
 * - Handle navigation to the campaign offer editor when editing is requested.
 *
 * Key methods:
 * - offers (getter): Produces an array of Offer objects derived from the input Campaign, mapping fields and deriving status.
 * - getStatusClass(status: string): Returns a CSS class name corresponding to the campaign status ('active' | 'scheduled' | 'expired').
 * - getStatusDisplay(status: string): Returns a human-friendly label for a campaign status.
 * - removeUnderscores(value: string): Replaces underscores with spaces for display purposes.
 * - getLocationTooltipText(): Builds a newline-separated list of branch names or falls back to the campaign's location string.
 * - onEditClick(): Navigates to the campaign editor route with the campaignId passed as a query parameter.
 *
 * Important notes / lifecycle:
 * - No explicit Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.) are implemented in this component; it relies on the campaign @Input being provided by the parent.
 * - The Router is injected via the constructor for imperative navigation to the campaign editor.
 */
@Component({
  selector: 'app-campaign-offers',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './campaign-offers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-offers.component.scss'],
})
export class CampaignOffersComponent {
  @Input() campaign!: Campaign;

  constructor(private router: Router) {}

  // Generate offers data from campaign data
  /**
   * Returns an array with a single Offer object built from this.campaign, or an empty array if no campaign is present.
   * @example
   * offers()
   * [{ id: 'campaign-id', name: 'Offer Name', description: 'Offer description', offerType: 'type', discountValue: 50, promoCode: 'PROMO50', validUntil: '2026-12-31', carWashType: '', location: 'Location A, Location B', maxDiscountAmount: '', max_discount: 50, quantityLimit: 100, usage: 'single', usageLimit: '1000', status: 'active' }]
   * @param {{void}} {{none}} - No arguments required.
   * @returns {{Offer[]}} An array of Offer objects (empty if this.campaign is falsy).
   **/
  get offers(): Offer[] {
    if (!this.campaign) return [];

    return [
      {
        id: this.campaign.id,
        name: this.campaign.offer_name,
        description: this.campaign.offer_description,
        offerType: this.campaign.offer_type,
        discountValue: this.campaign.max_discount,
        promoCode: this.campaign.promo_code,
        validUntil: this.campaign.start_date,
        carWashType: this.campaign.offer_wash_type || '',
        location: this.campaign.location_list
          ? this.campaign.location_list.join(', ')
          : this.campaign.location,
        maxDiscountAmount: '',
        max_discount: this.campaign.max_discount,
        quantityLimit: this.campaign.quantity_limit,
        usage: this.campaign.usage_type,
        usageLimit: '1000',
        status:
          this.campaign.campaign_status === 'active'
            ? 'active'
            : this.campaign.campaign_status === 'scheduled'
              ? 'scheduled'
              : 'expired',
      },
    ];
  }

  getStatusClass(status: string): string {
    const statusClassMap: Record<string, string> = {
      active: 'status-active',
      scheduled: 'status-scheduled',
      expired: 'status-expired',
    };
    return statusClassMap[status] || '';
  }

  getStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'Active',
      scheduled: 'Scheduled',
      expired: 'Expired',
    };
    return statusMap[status] || status;
  }

  removeUnderscores(value: string): string {
    return value ? value.replace(/_/g, ' ') : value;
  }

  getLocationTooltipText(): string {
    if (this.campaign.locations && this.campaign.locations.length > 0) {
      return this.campaign.locations.map(location => `• ${location.branch}`).join('\n');
    }
    return this.campaign.location || '';
  }

  onEditClick(): void {
    this.router.navigate(['/campaign-editor', 'offers'], {
      queryParams: { campaignId: this.campaign.id },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
